import Player from '@vimeo/player';

export default class VimeoPlayer {
    constructor($el) {
        this.$el = $el;
        this.currentPlayer = null;

        this.attachToLightboxes = this.attachToLightboxes.bind(this);

        this.blockUnconsentedTriggers();
        this.attachToLightboxes();
        requestAnimationFrame(this.attachToLightboxes);
    }

    /**
     * Do not let FS Lightbox create a Vimeo iframe before consent is granted.
     */
    blockUnconsentedTriggers() {
        this.$el.querySelectorAll('a[data-fslightbox][href^="#"]').forEach(($trigger) => {
            const $source = document.querySelector($trigger.getAttribute('href'));

            if ( ! this.isVimeoSource($source)) {
                return;
            }

            $trigger.addEventListener('click', (event) => {
                if (this.hasVimeoConsent()) {
                    return;
                }

                event.preventDefault();
                event.stopImmediatePropagation();
                this.openConsentPanel();
            }, true);
        });
    }

    attachToLightboxes() {
        if ( ! window.fsLightboxInstances) {
            return;
        }

        Object.keys(window.fsLightboxInstances).forEach((key) => {
            const lightbox = window.fsLightboxInstances[key];

            if (
                lightbox.vimeoPlayerInitialized
                || ! this.lightboxHasVimeoSource(lightbox)
            ) {
                return;
            }

            lightbox.vimeoPlayerInitialized = true;

            this.wrapLightboxCallback(lightbox, 'onOpen', () => {
                this.installLightboxHooks(lightbox);
                this.playActiveVimeoSource(lightbox);
            });

            this.wrapLightboxCallback(lightbox, 'onShow', () => {
                this.installLightboxHooks(lightbox);
                this.playActiveVimeoSource(lightbox);
            });

            this.wrapLightboxCallback(lightbox, 'onClose', () => {
                this.currentPlayer = null;
            });
        });
    }

    lightboxHasVimeoSource(lightbox) {
        return lightbox.props.sources.some(($source) => (
            this.isVimeoSource($source)
        ));
    }

    wrapLightboxCallback(lightbox, callbackName, callback) {
        const originalCallback = lightbox.props[callbackName];

        lightbox.props[callbackName] = (...args) => {
            if (typeof originalCallback === 'function') {
                originalCallback(...args);
            }

            callback(...args);
        };
    }

    installLightboxHooks(lightbox) {
        this.installSlideChangeHook(lightbox);
        this.installCloseHook(lightbox);
    }

    installSlideChangeHook(lightbox) {
        const slideIndexChanger = lightbox.core.slideIndexChanger;

        if (
            lightbox.vimeoSlideChangeHookInstalled
            || typeof slideIndexChanger.changeTo !== 'function'
        ) {
            return;
        }

        const originalChangeTo = slideIndexChanger.changeTo;

        slideIndexChanger.changeTo = (index) => {
            this.stopCurrentPlayer();

            const result = originalChangeTo(index);

            requestAnimationFrame(() => this.playActiveVimeoSource(lightbox));

            return result;
        };

        lightbox.vimeoSlideChangeHookInstalled = true;
    }

    installCloseHook(lightbox) {
        const lightboxCloser = lightbox.core.lightboxCloser;

        if (
            lightbox.vimeoCloseHookInstalled
            || typeof lightboxCloser.closeLightbox !== 'function'
        ) {
            return;
        }

        const originalCloseLightbox = lightboxCloser.closeLightbox;

        lightboxCloser.closeLightbox = () => {
            this.stopCurrentPlayer();

            return originalCloseLightbox();
        };

        lightbox.vimeoCloseHookInstalled = true;
    }

    playActiveVimeoSource(lightbox) {
        const $source = this.getActiveVimeoSource(lightbox);

        if ( ! $source) {
            this.stopCurrentPlayer();

            return;
        }

        const player = this.getPlayer($source);

        if ( ! player) {
            return;
        }

        if (this.currentPlayer && this.currentPlayer !== player) {
            this.stopCurrentPlayer();
        }

        this.currentPlayer = player;
        this.handlePlayerPromise(
            player.ready()
                .then(() => player.setAutopause(false))
                .then(() => player.play())
        );
    }

    getActiveVimeoSource(lightbox) {
        const index = lightbox.stageIndexes.current;
        const $source = lightbox.elements.sources[index];

        if ( ! this.isVimeoSource($source)) {
            return null;
        }

        return $source;
    }

    isVimeoSource($source) {
        return $source
            && typeof $source.matches === 'function'
            && $source.matches('[data-vimeo-lightbox-player]');
    }

    getPlayer($source) {
        if ( ! this.hasVimeoConsent()) {
            this.openConsentPanel();

            return null;
        }

        if ($source.player) {
            return $source.player;
        }

        if ( ! $source.dataset.vimeoLightboxUrl) {
            return null;
        }

        if ( ! $source.getAttribute('src')) {
            const embedUrl = this.getEmbedUrl($source.dataset.vimeoLightboxUrl);

            if ( ! embedUrl) {
                return null;
            }

            $source.setAttribute('src', embedUrl);
        }

        $source.player = new Player($source);

        return $source.player;
    }

    hasVimeoConsent() {
        return Boolean(
            window.tarteaucitron
            && window.tarteaucitron.state
            && window.tarteaucitron.state.vimeo === true
        );
    }

    openConsentPanel() {
        if (
            window.tarteaucitron
            && window.tarteaucitron.userInterface
            && typeof window.tarteaucitron.userInterface.openPanel === 'function'
        ) {
            window.tarteaucitron.userInterface.openPanel();
        }
    }

    getEmbedUrl(url) {
        let parsedUrl;

        try {
            parsedUrl = new URL(url, window.location.origin);
        } catch (error) {
            return null;
        }

        const isVimeoEmbed = parsedUrl.hostname === 'player.vimeo.com'
            && parsedUrl.pathname.indexOf('/video/') === 0;
        const embedUrl = isVimeoEmbed
            ? parsedUrl
            : this.createEmbedUrlFromVimeoUrl(parsedUrl);

        if ( ! embedUrl) {
            return null;
        }

        embedUrl.searchParams.set('autoplay', '1');
        embedUrl.searchParams.set('controls', '1');
        embedUrl.searchParams.set('loop', '0');
        embedUrl.searchParams.set('playsinline', '1');
        embedUrl.searchParams.set('dnt', '1');

        return embedUrl.toString();
    }

    createEmbedUrlFromVimeoUrl(parsedUrl) {
        const pathParts = parsedUrl.pathname.split('/').filter(Boolean);
        const videoId = pathParts.find((part) => /^\d+$/.test(part));

        if ( ! videoId) {
            return null;
        }

        const hash = parsedUrl.searchParams.get('h') || this.getVimeoHash(pathParts, videoId);
        const embedUrl = new URL(`https://player.vimeo.com/video/${videoId}`);

        if (hash) {
            embedUrl.searchParams.set('h', hash);
        }

        return embedUrl;
    }

    getVimeoHash(pathParts, videoId) {
        const videoIdIndex = pathParts.indexOf(videoId);
        const hash = pathParts[videoIdIndex + 1];

        if ( ! hash || /^\d+$/.test(hash)) {
            return null;
        }

        return hash;
    }

    stopCurrentPlayer() {
        if ( ! this.currentPlayer) {
            return;
        }

        const player = this.currentPlayer;
        this.currentPlayer = null;

        this.handlePlayerPromise(
            player.pause().then(() => player.setCurrentTime(0))
        );
    }

    handlePlayerPromise(promise) {
        if (promise && typeof promise.catch === 'function') {
            promise.catch(() => {});
        }
    }
}
